import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { login as loginRequest, getMe, AuthUser } from '../services/auth'

/**
 * H4 del roadmap (Fase B #5): login antes de compartir el dashboard con el
 * equipo (Alfonso/Elizabeth/Sylwester).
 */
interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('authToken')
    if (!token) {
      setLoading(false)
      return
    }

    // No basta con que el token exista en localStorage: puede haber
    // caducado, o el usuario pudo haber sido borrado — se valida contra
    // el backend antes de dar la sesión por buena. Si getMe() falla, el
    // interceptor de axios (services/api.ts) ya limpia localStorage en un
    // 401; aquí solo hace falta reflejarlo en el estado de React.
    getMe()
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  const login = async (email: string, password: string) => {
    const res = await loginRequest(email, password)
    localStorage.setItem('authToken', res.data.token)
    setUser(res.data.user)
  }

  const logout = () => {
    localStorage.removeItem('authToken')
    setUser(null)
  }

  return <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>')
  }
  return ctx
}
